/**
 * Keşif wizard — five steps from a bare domain to a running crawl.
 *
 * Uses `components/library/ui.tsx` primitives, not `components/common/`'s
 * glass system: this screen lives under `AppShell` alongside Personas and
 * Yönetim, and mixing the two design languages on one page reads as a bug.
 *
 * The step split is not cosmetic. Scope, politeness and extraction are three
 * independent decisions with different blast radii — scope decides what data
 * you get, politeness decides whether the target's operator notices, and
 * extraction decides how much disk it costs. One long form invites the user to
 * skim past the politeness gates, which are the ones with legal weight.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Globe,
  Loader2,
  Plus,
  Rocket,
  Trash2,
} from "lucide-react";
import {
  ASSET_KIND_LABELS,
  CRAWL_LIMITS,
  SCOPE_LABELS,
  formatDuration,
  type CrawlAssetKind,
  type CrawlPolicy,
  type CrawlRenderMode,
  type CrawlScopeMode,
} from "../../domain/kesif";
import { CrawlApiError, createCrawlJob, preflightCrawlUrl } from "../../transport/crawlClient";
import {
  Btn,
  Chip,
  ErrorBanner,
  Field,
  SectionTitle,
  Select,
  TextArea,
  TextInput,
  Toggle,
} from "../library/ui";
import {
  WIZARD_STEPS,
  emptyDraft,
  estimateMinutes,
  estimateRequests,
  isProbing,
  isStepValid,
  newSeed,
  probedHosts,
  stepIssues,
  usableSeeds,
  validatePattern,
  type SeedEntry,
  type WizardDraft,
  type WizardStep,
} from "./wizardModel";

/** Long enough that typing a domain does not fire a probe per character. */
const PREFLIGHT_DEBOUNCE_MS = 450;

export function KesifWizard({
  onCreated,
  onCancel,
}: {
  onCreated: (jobId: string, started: boolean) => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<WizardStep>(1);
  const [draft, setDraft] = useState<WizardDraft>(emptyDraft);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reduce = useReducedMotion();

  const patch = useCallback((next: Partial<WizardDraft>) => {
    setDraft((d) => ({ ...d, ...next }));
  }, []);
  const patchPolicy = useCallback((next: Partial<CrawlPolicy>) => {
    setDraft((d) => ({ ...d, policy: { ...d.policy, ...next } }));
  }, []);
  // Seeds mutate from debounced callbacks that outlive the render they were
  // scheduled in, so they need the LATEST array, not a captured one.
  const updateSeeds = useCallback((fn: (seeds: SeedEntry[]) => SeedEntry[]) => {
    setDraft((d) => ({ ...d, seeds: fn(d.seeds) }));
  }, []);

  const issues = useMemo(() => stepIssues(step, draft), [step, draft]);
  const canAdvance = issues.length === 0 && !isProbing(draft);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await createCrawlJob({
        name: draft.name.trim() || usableSeeds(draft)[0] || "Adsız tarama",
        seedUrls: usableSeeds(draft),
        policy: draft.policy,
        autoStart: draft.autoStart,
      });
      onCreated(res.job.id, res.started);
    } catch (err) {
      // 422 carries the offending field in `details`; without surfacing it the
      // user sees "request validation failed" and has nothing to act on.
      if (err instanceof CrawlApiError && Array.isArray(err.details)) {
        const first = err.details[0] as { loc?: unknown[]; msg?: string } | undefined;
        const where = Array.isArray(first?.loc) ? first?.loc.join(".") : "";
        setError(`${err.message}${where ? ` — ${where}: ${first?.msg ?? ""}` : ""}`);
      } else {
        setError(err instanceof Error ? err.message : "Tarama oluşturulamadı.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      <StepRail step={step} draft={draft} onJump={setStep} />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={reduce ? false : { opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, x: -12 }}
          transition={{ duration: 0.16 }}
          className="min-h-[380px]"
        >
          {step === 1 && (
            <StepTarget draft={draft} patch={patch} updateSeeds={updateSeeds} />
          )}
          {step === 2 && <StepScope draft={draft} patchPolicy={patchPolicy} />}
          {step === 3 && <StepPoliteness draft={draft} patchPolicy={patchPolicy} />}
          {step === 4 && <StepExtraction draft={draft} patchPolicy={patchPolicy} />}
          {step === 5 && <StepSummary draft={draft} patch={patch} />}
        </motion.div>
      </AnimatePresence>

      {issues.length > 0 && (
        <div className="space-y-1.5 rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2.5">
          {issues.slice(0, 4).map((i) => (
            <div
              key={`${i.field}-${i.message}`}
              className="flex items-start gap-2 text-[11.5px] text-amber-200"
            >
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
              <span className="leading-relaxed">{i.message}</span>
            </div>
          ))}
        </div>
      )}

      <footer className="flex items-center justify-between border-t border-white/[0.07] pt-4">
        <Btn tone="ghost" onClick={step === 1 ? onCancel : () => setStep((s) => (s - 1) as WizardStep)}>
          <ChevronLeft className="h-3.5 w-3.5" />
          {step === 1 ? "Vazgeç" : "Geri"}
        </Btn>
        <span className="text-[11px] text-slate-500">
          Adım {step} / {WIZARD_STEPS.length}
        </span>
        {step < 5 ? (
          <Btn
            tone="primary"
            disabled={!canAdvance}
            onClick={() => setStep((s) => (s + 1) as WizardStep)}
          >
            İleri
            <ChevronRight className="h-3.5 w-3.5" />
          </Btn>
        ) : (
          <Btn tone="primary" disabled={!canAdvance} loading={submitting} onClick={submit}>
            <Rocket className="h-3.5 w-3.5" />
            {draft.autoStart ? "Taramayı başlat" : "İşi oluştur"}
          </Btn>
        )}
      </footer>
    </div>
  );
}

/* ---------- step rail ---------- */

function StepRail({
  step,
  draft,
  onJump,
}: {
  step: WizardStep;
  draft: WizardDraft;
  onJump: (s: WizardStep) => void;
}) {
  return (
    <ol className="flex items-stretch gap-1.5">
      {WIZARD_STEPS.map((s) => {
        const done = s.step < step && isStepValid(s.step, draft);
        const active = s.step === step;
        // Backwards navigation only. Jumping FORWARD past an invalid step would
        // let the user reach the summary with a policy the backend will reject.
        const reachable = s.step <= step;
        return (
          <li key={s.step} className="flex-1">
            <button
              type="button"
              disabled={!reachable}
              onClick={() => reachable && onJump(s.step)}
              className={[
                "w-full rounded-lg border px-2.5 py-2 text-left transition-colors",
                active
                  ? "border-violet-500/50 bg-violet-500/10"
                  : done
                    ? "border-emerald-500/25 bg-emerald-500/[0.06] hover:bg-emerald-500/10"
                    : "border-white/[0.07] bg-white/[0.015]",
                reachable ? "cursor-pointer" : "cursor-default opacity-60",
              ].join(" ")}
            >
              <span className="flex items-center gap-1.5">
                <span
                  className={[
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold",
                    active
                      ? "bg-violet-500 text-white"
                      : done
                        ? "bg-emerald-500 text-white"
                        : "bg-white/10 text-slate-400",
                  ].join(" ")}
                >
                  {done ? <Check className="h-2.5 w-2.5" /> : s.step}
                </span>
                <span
                  className={`truncate text-[11.5px] font-medium ${active ? "text-white" : "text-slate-400"}`}
                >
                  {s.title}
                </span>
              </span>
              <span className="mt-0.5 block truncate text-[10px] text-slate-500">
                {s.hint}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/* ---------- step 1: target ---------- */

function StepTarget({
  draft,
  patch,
  updateSeeds,
}: {
  draft: WizardDraft;
  patch: (next: Partial<WizardDraft>) => void;
  updateSeeds: (fn: (seeds: SeedEntry[]) => SeedEntry[]) => void;
}) {
  // One timer per seed. A single shared timer would cancel the probe of seed A
  // the moment the user starts typing seed B.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(
    () => () => {
      for (const t of timers.current.values()) clearTimeout(t);
      timers.current.clear();
    },
    [],
  );

  const setSeed = useCallback(
    (id: string, next: Partial<SeedEntry>) => {
      updateSeeds((seeds) => seeds.map((s) => (s.id === id ? { ...s, ...next } : s)));
    },
    [updateSeeds],
  );

  function onSeedChange(id: string, raw: string) {
    const existing = timers.current.get(id);
    if (existing) clearTimeout(existing);

    const probeable = raw.trim().length > 2;
    setSeed(id, { raw, probe: null, checking: probeable });
    if (!probeable) return;

    timers.current.set(
      id,
      setTimeout(async () => {
        timers.current.delete(id);
        try {
          const probe = await preflightCrawlUrl(raw.trim());
          setSeed(id, { probe, checking: false });
        } catch {
          // Transport failure is not a verdict on the URL — leave it unprobed
          // so the user can still submit and let the backend decide.
          setSeed(id, { probe: null, checking: false });
        }
      }, PREFLIGHT_DEBOUNCE_MS),
    );
  }

  function removeSeed(id: string) {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    updateSeeds((seeds) => seeds.filter((s) => s.id !== id));
  }

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Başlangıç adresleri"
        hint="Alan adı yazmanız yeterli — şema otomatik tamamlanır. Her adres yazıldıkça sunucuda taranabilirlik kontrolü yapılır."
      />

      <Field label="Tarama adı" hint="Boş bırakılırsa ilk adres kullanılır.">
        <TextInput
          value={draft.name}
          maxLength={200}
          placeholder="örn. Rakip fiyat listesi"
          onChange={(e) => patch({ name: e.target.value })}
        />
      </Field>

      <div className="space-y-2">
        {draft.seeds.map((seed) => (
          <div key={seed.id} className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Globe className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-600" />
                <TextInput
                  value={seed.raw}
                  placeholder="example.com veya https://example.com/blog"
                  className="pl-8"
                  onChange={(e) => onSeedChange(seed.id, e.target.value)}
                />
              </div>
              {seed.checking && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
              {!seed.checking && seed.probe?.ok && (
                <Chip tone="green" title={seed.probe.url}>
                  <Check className="h-2.5 w-2.5" />
                  {seed.probe.host}
                </Chip>
              )}
              {!seed.checking && seed.probe && !seed.probe.ok && (
                <Chip tone="rose">Taranamaz</Chip>
              )}
              <Btn
                tone="ghost"
                size="sm"
                disabled={draft.seeds.length === 1}
                onClick={() => removeSeed(seed.id)}
                aria-label="Adresi kaldır"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Btn>
            </div>
            {seed.probe && !seed.probe.ok && seed.probe.message && (
              <p className="pl-8 text-[11px] leading-snug text-rose-300/80">
                {seed.probe.message}
              </p>
            )}
          </div>
        ))}
      </div>

      <Btn
        tone="outline"
        size="sm"
        disabled={draft.seeds.length >= CRAWL_LIMITS.seeds.max}
        onClick={() => updateSeeds((seeds) => [...seeds, newSeed()])}
      >
        <Plus className="h-3.5 w-3.5" />
        Adres ekle
      </Btn>

      <Field label="Amaç" hint="Ne aradığınızı yazın; LLM planlama ve link önceliklendirmesi bunu kullanır. Boş bırakılırsa tamamen sezgisel tarama yapılır.">
        <TextArea
          rows={3}
          maxLength={2000}
          value={draft.policy.intent}
          placeholder="örn. Ürün sayfalarındaki fiyat ve stok bilgisi; blog yazıları önemli değil."
          onChange={(e) =>
            patch({ policy: { ...draft.policy, intent: e.target.value } })
          }
        />
      </Field>
    </div>
  );
}

/* ---------- step 2: scope ---------- */

function StepScope({
  draft,
  patchPolicy,
}: {
  draft: WizardDraft;
  patchPolicy: (next: Partial<CrawlPolicy>) => void;
}) {
  const p = draft.policy;
  const hosts = probedHosts(draft);

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Kapsam ve bütçe"
        hint="Kapsam veri kümesinin sınırını, bütçe ise maliyetini belirler. İkisi de tarama başladıktan sonra daraltılamaz."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Kapsam" hint={SCOPE_HINTS[p.scopeMode]}>
          <Select
            value={p.scopeMode}
            onChange={(e) => {
              const mode = e.target.value as CrawlScopeMode;
              patchPolicy({
                scopeMode: mode,
                // Prefilling from the probed seeds turns a dead-end validation
                // error into a working default the user can edit.
                ...(mode === "allowlist" && p.allowedDomains.length === 0
                  ? { allowedDomains: hosts }
                  : {}),
              });
            }}
          >
            {(Object.keys(SCOPE_LABELS) as CrawlScopeMode[]).map((m) => (
              <option key={m} value={m}>
                {SCOPE_LABELS[m]}
              </option>
            ))}
          </Select>
        </Field>

        <NumberField
          label="Dış alan adı sıçraması"
          hint="0 = kapsam dışına asla çıkma. 1 = dış bir linki izle ama o sayfadaki linkleri izleme. Derinlik bunu ifade edemez."
          value={p.maxExternalHops}
          min={CRAWL_LIMITS.maxExternalHops.min}
          max={CRAWL_LIMITS.maxExternalHops.max}
          onChange={(v) => patchPolicy({ maxExternalHops: v })}
        />

        <NumberField
          label="Maksimum derinlik"
          hint="Başlangıç adresinden kaç link uzağa gidilecek."
          value={p.maxDepth}
          min={CRAWL_LIMITS.maxDepth.min}
          max={CRAWL_LIMITS.maxDepth.max}
          onChange={(v) => patchPolicy({ maxDepth: v })}
        />

        <NumberField
          label="Maksimum sayfa"
          hint="Sunucu tavanı bunu daha da düşürebilir; gerçek değer işte görünür."
          value={p.maxPages}
          min={CRAWL_LIMITS.maxPages.min}
          max={CRAWL_LIMITS.maxPages.max}
          onChange={(v) => patchPolicy({ maxPages: v })}
        />

        <NumberField
          label="Host başına sayfa"
          hint="Tek bir alan adının tüm bütçeyi tüketmesini engeller."
          value={p.maxPagesPerHost}
          min={CRAWL_LIMITS.maxPagesPerHost.min}
          max={CRAWL_LIMITS.maxPagesPerHost.max}
          onChange={(v) => patchPolicy({ maxPagesPerHost: v })}
        />

        <NumberField
          label="Süre sınırı (dk)"
          value={p.maxDurationMinutes}
          min={CRAWL_LIMITS.maxDurationMinutes.min}
          max={CRAWL_LIMITS.maxDurationMinutes.max}
          onChange={(v) => patchPolicy({ maxDurationMinutes: v })}
        />
      </div>

      {p.scopeMode === "allowlist" && (
        <DomainListField
          label="İzin verilen alan adları"
          hint="Yalnızca bu alan adları taranır. Her satıra bir tane."
          value={p.allowedDomains}
          onChange={(v) => patchPolicy({ allowedDomains: v })}
        />
      )}

      <DomainListField
        label="Engellenen alan adları"
        hint="Kapsam ne olursa olsun bunlar asla taranmaz — engelleme kapsamı ezer."
        value={p.blockedDomains}
        onChange={(v) => patchPolicy({ blockedDomains: v })}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <PatternListField
          label="Yalnızca şu desenler (regex)"
          hint="Boşsa tüm yollar aday. Bir desen verilirse eşleşmeyen yollar atlanır."
          value={p.includePatterns}
          onChange={(v) => patchPolicy({ includePatterns: v })}
        />
        <PatternListField
          label="Hariç tutulan desenler (regex)"
          hint="Dahil etme desenlerinden ÖNCE uygulanır."
          value={p.excludePatterns}
          onChange={(v) => patchPolicy({ excludePatterns: v })}
        />
      </div>

      <Toggle
        checked={p.followSitemaps}
        onChange={(v) => patchPolicy({ followSitemaps: v })}
        label="Sitemap'leri kullan"
        hint="robots.txt'de bildirilen sitemap'ler okunur — link keşfinden çok daha eksiksiz bir başlangıç kuyruğu verir."
      />
    </div>
  );
}

const SCOPE_HINTS: Record<CrawlScopeMode, string> = {
  "exact-host": "Yalnızca girilen host. www ve alt alan adları hariç.",
  subdomains: "Alan adı ve tüm alt alan adları (blog.example.com dahil).",
  "registrable-domain": "Kayıt edilebilir alan adının tamamı.",
  allowlist: "Yalnızca aşağıda listelenen alan adları.",
  unrestricted: "Her yere gidilebilir — yalnızca dış sıçrama sınırı ile durur.",
};

/* ---------- step 3: politeness ---------- */

function StepPoliteness({
  draft,
  patchPolicy,
}: {
  draft: WizardDraft;
  patchPolicy: (next: Partial<CrawlPolicy>) => void;
}) {
  const p = draft.policy;
  const needsOwnership =
    !p.respectRobots || p.requestsPerSecond > CRAWL_LIMITS.rpsWithoutOwnership;

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Nezaket ve yetki"
        hint="Bu adımdaki iki ayar hedef sitenin operatörünü doğrudan etkiler ve hukuki sorumluluk taşır."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={`Hız: ${p.requestsPerSecond.toFixed(1)} istek/sn`}
          hint="Host başına. Sunucunun bildirdiği Crawl-delay daha yavaşsa o kazanır; 429/503 görülürse otomatik yavaşlanır."
        >
          <input
            type="range"
            min={0.1}
            max={CRAWL_LIMITS.requestsPerSecond.max}
            step={0.1}
            value={p.requestsPerSecond}
            onChange={(e) => patchPolicy({ requestsPerSecond: Number(e.target.value) })}
            className="w-full accent-violet-500"
          />
        </Field>

        <Field label="Render modu" hint={RENDER_HINTS[p.renderMode]}>
          <Select
            value={p.renderMode}
            onChange={(e) => patchPolicy({ renderMode: e.target.value as CrawlRenderMode })}
          >
            <option value="auto">Otomatik (önerilen)</option>
            <option value="http-only">Yalnızca HTTP</option>
            <option value="browser-always">Her zaman tarayıcı</option>
          </Select>
        </Field>
      </div>

      <div className="space-y-3 rounded-lg border border-white/[0.07] bg-white/[0.015] p-3.5">
        <Toggle
          checked={p.respectRobots}
          onChange={(v) => patchPolicy({ respectRobots: v })}
          label="robots.txt kurallarına uy"
          hint="RFC 9309. Kapatmak yalnızca kendi sitenizde meşrudur ve işe kalıcı olarak kaydedilir."
        />
        {needsOwnership && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-3">
            <Toggle
              checked={p.ownerDeclared}
              onChange={(v) => patchPolicy({ ownerDeclared: v })}
              label="Bu alan adlarının sahibiyim veya taramaya yetkiliyim"
              hint="Bu beyan sorumluluğu size aktarır ve iş kaydında saklanır. Beyan olmadan robots.txt kapatılamaz ve 2 istek/sn üzerine çıkılamaz."
            />
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField
          label="Önbellek ömrü (saat)"
          hint="Bu süreden yeni bir yakalama varsa istek hiç atılmaz. 0 = her zaman yeniden çek. Boş = sunucu varsayılanı."
          value={p.recrawlTtlHours ?? -1}
          min={-1}
          max={CRAWL_LIMITS.recrawlTtlHours.max}
          allowInherit
          onChange={(v) => patchPolicy({ recrawlTtlHours: v < 0 ? null : v })}
        />
        <Toggle
          checked={p.adaptiveStop}
          onChange={(v) => patchPolicy({ adaptiveStop: v })}
          label="Doygunlukta erken dur"
          hint="Yeni sayfalar yeni bilgi taşımamaya başladığında tarama kendiliğinden durur — bütçe boşa harcanmaz."
        />
      </div>
    </div>
  );
}

const RENDER_HINTS: Record<CrawlRenderMode, string> = {
  auto: "HTTP ile dene; boş kabuk veya bot duvarı görülürse gerçek tarayıcıya yükselt.",
  "http-only": "Tarayıcı hiç kullanılmaz. Statik siteler için en hızlısı.",
  "browser-always": "Her sayfa gerçek tarayıcıda açılır. JS ile çizilen siteler için; çok daha yavaş.",
};

/* ---------- step 4: extraction ---------- */

function StepExtraction({
  draft,
  patchPolicy,
}: {
  draft: WizardDraft;
  patchPolicy: (next: Partial<CrawlPolicy>) => void;
}) {
  const p = draft.policy;
  const [schemaText, setSchemaText] = useState(() =>
    p.extractSchema ? JSON.stringify(p.extractSchema, null, 2) : "",
  );
  const [schemaError, setSchemaError] = useState<string | null>(null);

  function onSchemaChange(text: string) {
    setSchemaText(text);
    if (!text.trim()) {
      setSchemaError(null);
      patchPolicy({ extractSchema: null });
      return;
    }
    try {
      const parsed = JSON.parse(text) as unknown;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setSchemaError("Şema bir JSON nesnesi olmalı.");
        return;
      }
      setSchemaError(null);
      patchPolicy({ extractSchema: parsed as Record<string, unknown> });
    } catch (err) {
      setSchemaError(err instanceof Error ? err.message : "Geçersiz JSON.");
    }
  }

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Çıkarım ve çıktı"
        hint="Ne toplanacağı ve toplananın nereye gideceği. Varlıklar disk kotasından harcar."
      />

      <div className="space-y-3 rounded-lg border border-white/[0.07] bg-white/[0.015] p-3.5">
        <Toggle
          checked={p.extractAssets}
          onChange={(v) => patchPolicy({ extractAssets: v })}
          label="Varlıkları indir"
          hint="Görsel, PDF ve belgeler diske alınır; içerik adresli depolama sayesinde aynı dosya iki kez yazılmaz."
        />
        {p.extractAssets && (
          <div className="flex flex-wrap gap-1.5 pl-12">
            {(Object.keys(ASSET_KIND_LABELS) as CrawlAssetKind[]).map((kind) => {
              const on = p.assetKinds.includes(kind);
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() =>
                    patchPolicy({
                      assetKinds: on
                        ? p.assetKinds.filter((k) => k !== kind)
                        : [...p.assetKinds, kind],
                    })
                  }
                  className={[
                    "rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors",
                    on
                      ? "border-violet-500/40 bg-violet-500/15 text-violet-200"
                      : "border-white/10 bg-white/[0.03] text-slate-500 hover:text-slate-300",
                  ].join(" ")}
                >
                  {ASSET_KIND_LABELS[kind]}
                </button>
              );
            })}
          </div>
        )}
        {p.extractAssets && (p.assetKinds.includes("video") || p.assetKinds.includes("audio")) && (
          <p className="pl-12 text-[11px] leading-snug text-amber-300/80">
            Video/ses tek bir dosyayla iş başına disk kotasını doldurabilir.
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Toggle
          checked={p.extractTables}
          onChange={(v) => patchPolicy({ extractTables: v })}
          label="Tabloları çıkar"
          hint="HTML tabloları yapılandırılmış olarak saklanır."
        />
        <Toggle
          checked={p.extractStructuredData}
          onChange={(v) => patchPolicy({ extractStructuredData: v })}
          label="JSON-LD / OpenGraph oku"
          hint="Sayfanın kendi bildirdiği yapılandırılmış veri — LLM'den çok daha güvenilir."
        />
        <NumberField
          label="Minimum metin (karakter)"
          hint="Altında kalan sayfalar 'ince' işaretlenir: kaydedilir ama kanıt korpusuna gönderilmez."
          value={p.minTextChars}
          min={CRAWL_LIMITS.minTextChars.min}
          max={CRAWL_LIMITS.minTextChars.max}
          onChange={(v) => patchPolicy({ minTextChars: v })}
        />
        <Toggle
          checked={p.captureScreenshots}
          onChange={(v) => patchPolicy({ captureScreenshots: v })}
          label="Ekran görüntüsü al"
          hint="Yalnızca tarayıcı katmanına düşen sayfalar için. Belirgin şekilde yavaşlatır."
        />
      </div>

      <div className="space-y-3 rounded-lg border border-white/[0.07] bg-white/[0.015] p-3.5">
        <Toggle
          checked={p.llmPlan}
          onChange={(v) => patchPolicy({ llmPlan: v })}
          label="LLM ile plan çıkar"
          hint="Tarama başında bir kez çalışır: site tipini tanır, öncelik anahtar kelimeleri ve desen önerir. Öneriler otomatik uygulanmaz."
        />
        <Toggle
          checked={p.llmPrioritise}
          onChange={(v) => patchPolicy({ llmPrioritise: v })}
          label="LLM ile link önceliklendir"
          hint="Tarama ortasında anahtar kelimeleri günceller. Link başına LLM çağrısı YAPILMAZ — maliyet iş başına sabittir."
        />
        <Toggle
          checked={p.llmExtract}
          onChange={(v) => patchPolicy({ llmExtract: v })}
          label="Şemalı çıkarım"
          hint="Her yüksek değerli sayfadan verdiğiniz JSON şemasına uygun alanlar çıkarılır. Sayfa başına LLM çağrısı demektir."
        />
        {p.llmExtract && (
          <div className="pl-12">
            <Field label="JSON şeması" hint="Alan adları ve tipleri. Yalnızca şekil doğrulanır, anlam değil.">
              <TextArea
                rows={6}
                value={schemaText}
                spellCheck={false}
                placeholder={'{\n  "fiyat": "number",\n  "stok": "boolean"\n}'}
                className="font-mono text-[11.5px]"
                onChange={(e) => onSchemaChange(e.target.value)}
              />
            </Field>
            {schemaError && (
              <p className="mt-1 text-[11px] text-rose-300/80">{schemaError}</p>
            )}
          </div>
        )}
      </div>

      <Toggle
        checked={p.pushToCorpus}
        onChange={(v) => patchPolicy({ pushToCorpus: v })}
        label="Kanıt korpusuna gönder"
        hint="Toplanan sayfalar tartışmalarda kanıt olarak kullanılabilir hâle gelir (pgvector ile aranır)."
      />
    </div>
  );
}

/* ---------- step 5: summary ---------- */

function StepSummary({
  draft,
  patch,
}: {
  draft: WizardDraft;
  patch: (next: Partial<WizardDraft>) => void;
}) {
  const p = draft.policy;
  const seeds = usableSeeds(draft);
  const requests = estimateRequests(p);
  const minutes = estimateMinutes(p);

  return (
    <div className="space-y-5">
      <SectionTitle
        title="Özet"
        hint="Başlatmadan önce hedef siteye ne kadar yük bineceğini görün."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Başlangıç adresi" value={String(seeds.length)} />
        <Stat label="Sayfa bütçesi" value={p.maxPages.toLocaleString("tr-TR")} />
        <Stat label="Derinlik" value={String(p.maxDepth)} />
      </div>

      {/* The honest number: pages alone understate the load whenever assets are
          on, and an image-heavy site multiplies it by an order of magnitude. */}
      <div className="rounded-lg border border-white/[0.07] bg-white/[0.015] p-3.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[12px] text-slate-300">Hedef siteye tahmini yük</span>
          <span className="text-[13px] font-semibold text-white">
            ~{requests.toLocaleString("tr-TR")} istek
          </span>
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
          {p.requestsPerSecond.toFixed(1)} istek/sn hızında en fazla{" "}
          {formatDuration(minutes * 60)} sürer. Varlık indirmeleri de istek sayılır;
          {p.extractAssets ? " varlık toplama açık." : " varlık toplama kapalı."}
        </p>
      </div>

      <dl className="divide-y divide-white/[0.06] overflow-hidden rounded-lg border border-white/[0.07]">
        <Row label="Kapsam" value={SCOPE_LABELS[p.scopeMode]} />
        <Row
          label="robots.txt"
          value={p.respectRobots ? "Uyuluyor" : "YOK SAYILIYOR (sahiplik beyan edildi)"}
          tone={p.respectRobots ? "neutral" : "rose"}
        />
        <Row label="Render" value={RENDER_HINTS[p.renderMode].split(".")[0] ?? p.renderMode} />
        <Row
          label="Varlıklar"
          value={
            p.extractAssets
              ? p.assetKinds.map((k) => ASSET_KIND_LABELS[k]).join(", ")
              : "Toplanmıyor"
          }
        />
        <Row
          label="LLM"
          value={
            [
              p.llmPlan ? "plan" : null,
              p.llmPrioritise ? "önceliklendirme" : null,
              p.llmExtract ? "şemalı çıkarım" : null,
            ]
              .filter(Boolean)
              .join(", ") || "Kapalı"
          }
        />
        <Row label="Korpus" value={p.pushToCorpus ? "Gönderiliyor" : "Gönderilmiyor"} />
        <Row
          label="Doygunlukta durma"
          value={p.adaptiveStop ? "Açık" : "Kapalı"}
        />
      </dl>

      <div className="space-y-2">
        {seeds.map((s) => (
          <div
            key={s}
            className="truncate rounded-lg border border-white/[0.07] bg-black/20 px-3 py-1.5 font-mono text-[11.5px] text-slate-300"
          >
            {s}
          </div>
        ))}
      </div>

      <Toggle
        checked={draft.autoStart}
        onChange={(v) => patch({ autoStart: v })}
        label="Oluşturunca hemen başlat"
        hint="Kapatırsanız iş 'sırada' oluşturulur; robots kararını ve planı görüp sonra başlatabilirsiniz."
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.015] px-3 py-2.5">
      <div className="text-[10.5px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-[16px] font-semibold text-white">{value}</div>
    </div>
  );
}

function Row({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "rose";
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2">
      <dt className="text-[11.5px] text-slate-500">{label}</dt>
      <dd
        className={`text-right text-[11.5px] ${tone === "rose" ? "font-semibold text-rose-300" : "text-slate-200"}`}
      >
        {value}
      </dd>
    </div>
  );
}

/* ---------- shared fields ---------- */

function NumberField({
  label,
  hint,
  value,
  min,
  max,
  allowInherit,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  /** `-1` renders as an empty input meaning "inherit the server default". */
  allowInherit?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={label} {...(hint ? { hint } : {})}>
      <TextInput
        type="number"
        min={min}
        max={max}
        value={allowInherit && value < 0 ? "" : value}
        placeholder={allowInherit ? "sunucu varsayılanı" : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(allowInherit ? -1 : min);
            return;
          }
          const n = Number(raw);
          if (!Number.isFinite(n)) return;
          onChange(Math.min(max, Math.max(allowInherit ? -1 : min, Math.round(n))));
        }}
      />
    </Field>
  );
}

/** Newline-separated editor. Normalisation matches the backend validator. */
function DomainListField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <Field label={label} {...(hint ? { hint } : {})}>
      <TextArea
        rows={2}
        value={value.join("\n")}
        spellCheck={false}
        placeholder="example.com"
        onChange={(e) =>
          onChange(
            e.target.value
              .split("\n")
              .map((s) =>
                s
                  .trim()
                  .toLowerCase()
                  .replace(/^\*\./, "")
                  .replace(/^www\./, "")
                  .split("/")[0]!
                  .split(":")[0]!,
              )
              .filter(Boolean)
              .slice(0, 64),
          )
        }
      />
    </Field>
  );
}

function PatternListField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const errors = value.map(validatePattern).filter((e): e is string => e !== null);
  return (
    <div className="space-y-1">
      <Field label={label} {...(hint ? { hint } : {})}>
        <TextArea
          rows={3}
          value={value.join("\n")}
          spellCheck={false}
          placeholder="/blog/&#10;\.pdf$"
          className="font-mono text-[11.5px]"
          onChange={(e) =>
            onChange(
              e.target.value
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean)
                .slice(0, CRAWL_LIMITS.patterns.max),
            )
          }
        />
      </Field>
      {errors.slice(0, 2).map((err) => (
        <p key={err} className="text-[11px] leading-snug text-rose-300/80">
          {err}
        </p>
      ))}
    </div>
  );
}
